import { useDispatch, useSelector } from "react-redux"
import Filters from "./filters/filters"
import TableView from "./atoms/TableView"
import { useEffect, useState } from "react"
import { convertLeadToEvent, getRentalAvailability, loadLeads } from "../interfaces/leadsInterface"
import { checkEqual, isoDateToDate, toDayjs } from "../helpers/helper"
import { clearLeadsInfo } from "../features/rentSlice"
import PersistantRef from "./atoms/PersistantRef"
import { Tag, Row, Col, Button, message , Modal} from "antd"
import { FormNavigator } from "../dynamicform/formNavigator"
import apiAction from "../ajax/service"
import { useNavigate } from "react-router-dom"
import CenteredDisplay from "./atoms/CenteredContent"


import { Avatar,  Alert} from 'antd'
import { CheckOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { updateFormData } from "../dynamicform/formInterface"

const ConvertToEvent = ({
  convertModalData,
  onCancel,
  onConvert,
}) => {
  const [availabilityResult,  setAvailabilityResult]  = useState(null)
  const [availabilityLoading, setAvailabilityLoading] = useState(false)
  const [convertLoading,      setConvertLoading]      = useState(false)

  const handleClose = () => {
    setAvailabilityResult(null)
    setAvailabilityLoading(false)
    setConvertLoading(false)
    onCancel()
  }

  const handleCheckAvailability = async () => {
  setAvailabilityLoading(true)
  try {
    const data = await getRentalAvailability(
      convertModalData.startDate,
      convertModalData.endDate,
      convertModalData.rentals.map(r => r.id)
    )

    const allAvailable = convertModalData.rentals.every(
      r => (data[r.id] ?? 0) >= convertModalData.rentalCounts[r.id]
    )

    setAvailabilityResult({
      ...data,
      available: allAvailable,
      message: allAvailable ? null : 'One or more items are unavailable for the selected dates.',
    })
  } catch {
    setAvailabilityResult({
      available: false,
      message: 'Could not check availability. Please try again.',
    })
  } finally {
    setAvailabilityLoading(false)
  }
}

  const handleConvert = async () => {
    setConvertLoading(true)
    try {
      await onConvert(convertModalData)
    } finally {
      setConvertLoading(false)
    }
  }

  if (!convertModalData) return null

  return (
    <>
      <div className="flex flex-col gap-4">

        {/* Customer */}
        <div className="flex items-center gap-3">
          <Avatar className="bg-blue-50 text-blue-700 font-medium flex-shrink-0">
            {convertModalData.name?.slice(0, 2).toUpperCase()}
          </Avatar>
          <div>
            <div className="text-sm font-medium text-gray-800">
              {convertModalData.name}
            </div>
            <div className="text-xs text-gray-400">
              {convertModalData.email} · {convertModalData.phone}
            </div>
          </div>
        </div>

        {/* Dates + Address */}
        <div className="bg-gray-50 rounded-lg p-3 grid grid-cols-2 gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-0.5">
              Start date
            </div>
            <div className="text-sm font-medium text-gray-800">
              {dayjs(convertModalData.startDate).format('D MMM YYYY')}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-0.5">
              End date
            </div>
            <div className="text-sm font-medium text-gray-800">
              {dayjs(convertModalData.endDate).format('D MMM YYYY')}
            </div>
          </div>
          <div className="col-span-2">
            <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-0.5">
              Address
            </div>
            <div className="text-sm font-medium text-gray-800">
              {convertModalData.address || '—'}
            </div>
          </div>
        </div>

        {/* Rental Items */}
        <div>
          <div className="text-xs text-gray-400 mb-2">Rental items</div>
          <div className="overflow-y-auto max-h-[300px]">
          <table className="w-full border border-gray-100 rounded-lg overflow-hidden text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-3 py-2 text-[11px] font-medium text-gray-400 text-left border-b border-gray-100">
                  Item
                </th>
                <th className="px-3 py-2 text-[11px] font-medium text-gray-400 text-center border-b border-gray-100 w-20">
                  Requested
                </th>
                {availabilityResult && (
                  <th className="px-3 py-2 text-[11px] font-medium text-gray-400 text-center border-b border-gray-100 w-20">
                    Available
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {convertModalData.rentals.map((r, i) => {
                const requested  = convertModalData.rentalCounts[r.id]

                const availQty   = availabilityResult?.[r.id]
                const sufficient = availQty >= requested
                return (
                  <tr
                    key={r.id}
                    className={i !== 0 ? 'border-t border-gray-100' : ''}
                  >
                    <td className="px-3 py-2.5 text-sm text-gray-700">{r.name}</td>
                    <td className="px-3 py-2.5 text-sm text-gray-400 text-center">
                      {requested}
                    </td>
                    {availabilityResult && (
                      <td className="px-3 py-2.5 text-center">
                        <Tag color={sufficient ? 'success' : 'error'}>{availQty}</Tag>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
          </div>
        </div>

        {/* Availability banner */}
        {availabilityResult && (
          availabilityResult.available
            ? <Alert type="success" showIcon message={<span className="text-xs">All items are available for the selected dates.</span>} />
            : <Alert type="error"   showIcon message={<span className="text-xs">{availabilityResult.message || 'One or more items are unavailable for the selected dates.'}</span>} />
        )}

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={handleClose}>Cancel</Button>
          {availabilityResult?.available ? (
            <Button
              type="primary"
              icon={<CheckOutlined />}
              loading={convertLoading}
              onClick={handleConvert}
            >
              Convert to event
            </Button>
          ) : (
            <Button
              type="primary"
              loading={availabilityLoading}
              onClick={handleCheckAvailability}
            >
              Check availability
            </Button>
          )}
        </div>

      </div>
   </>
  )
}


const LeadsView = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [selectedIds, setSelectedIds] = useState([])
    const leads = useSelector(state => state.rent.leads)
    const currentPage = useSelector(state => state.rent.leads.currentPage)
    const leadsFilters = useSelector(state => state.rent.leadsFilters)
    const [convertModal , setConvertModal] = useState(false)
    const [convertModalData , setConvertModalData] = useState({})

    const previousLeadsFilters = new PersistantRef("previousLeadsFilters");
    const formNavigator = FormNavigator(dispatch)

    useEffect(() => {
        if (!checkEqual(leadsFilters, previousLeadsFilters.getValue())) {
            dispatch(clearLeadsInfo());
            loadLeads(dispatch, undefined, undefined, new Set(Object.keys(leads)), leadsFilters)
            previousLeadsFilters.setValue(leadsFilters);
        }
    }, [leadsFilters])

    // UPDATED: converted action
    const handleConvertedAction = () => {
        let count = 0;
        selectedIds.forEach((leadId) => {
            apiAction("forms", { modal: "lead", converted: true, id: leadId }, "update")
                .then(() => {
                    message.success("Converted Lead " + leadId)
                    count++
                    if (count === selectedIds.length) {
                        dispatch(clearLeadsInfo())
                        loadLeads(dispatch, undefined, undefined, new Set(Object.keys(leads)), leadsFilters)
                        setSelectedIds([])
                    }
                })
        })
    }

    useEffect(() => {
        loadLeads(dispatch, undefined, undefined, new Set(Object.keys(leads)), leadsFilters)
    }, [])
    console.log(convertModalData)
    return (
        <>
        {!leads || !leads[currentPage]  || !leads[currentPage]?.length ? <CenteredDisplay timeout={7000} title={"No Service Request Created Yet"} description={"Create a Service Request to Get Started"}  actionText={"Add"} actionCallback={()=>formNavigator.lead(-1)}/>:
        <>
        <Modal
            open={convertModal}
            onCancel={() => setConvertModal(false)}
            footer={null}
            width={'60%'}
            title={
                <div>
                <div style={{ fontSize: 15, fontWeight: 500 }}>Convert to event</div>
                <div style={{ fontSize: 12, color: '#888', fontWeight: 400 }}>
                    Review details before confirming
                </div>
                </div>
            }
        >

            <ConvertToEvent
            open={convertModal}
            convertModalData={convertModalData}
            onCancel={() => setConvertModal(false)}
            onConvert={async(convertedModalData)=>{
               const convertedEventData =  await convertLeadToEvent(convertModalData)
               for (var rental of convertModalData.rentals){
               const formData = { action:      "update",
                    modal:       "rentalAgreement",
                    id:          convertedEventData.agreement.id,
                    eventId:     convertedEventData.event.id,
                    tenantId:    convertedEventData.tenant.id,
                    workspaceId: convertedEventData.agreement.workspaceId,
                    status:      convertedEventData.agreement.status,
                    startDate:   convertedEventData.event.startDate,
                    endDate:     convertedEventData.event.endDate,
                    rentalId:    rental.id,
                    quantity:    convertedModalData['rentalCounts'][rental.id],
                    amount:      convertedEventData?.defaultAmounts?.[rental.id] || -1,
                    }
                    await apiAction("forms",{...formData} , "update")
               }
               setConvertModal(false)
               navigate(`/event/${convertedEventData.event.id}`)
               await updateFormData('Lead',convertedModalData?.id,'converted',true)
               await loadLeads(dispatch, undefined, undefined, new Set(Object.keys(leads)), leadsFilters)


            }}

            />
        </Modal>
            <Row>
                <Col span={16}>
                    <Filters dispatch={dispatch} filterPage="leads" values={leadsFilters} />
                </Col>

                <Col span={8} className="m-auto">

                    {/* REMOVED ARCHIVE */}

                    {/* NEW: Converted Action */}
                    <Button
                        variant="solid"
                        disabled={selectedIds.length === 0}
                        color="green"
                        style={{ margin: 'auto', float: 'right' }}
                        onClick={handleConvertedAction}
                    >
                        Converted
                    </Button>

                    <Button
                        style={{ margin: 'auto', float: 'right', right: 10 }}
                        onClick={() => formNavigator.lead(-1)}
                    >
                        Create
                    </Button>
                </Col>
            </Row>

            <TableView
                editableCallback={({ id }) => formNavigator.lead(id)}
                rowKey={'id'}
                totalData={leads.totalData}
                pageLimit={25}
                data={leads[currentPage]}
                onlyFields={["name", "email", "rentals", "startDate","endDate", "phone", "converted"]}
                onPageChange={(pageNum) => {
                    loadLeads(dispatch, pageNum, 25, new Set(Object.keys(leads)), leadsFilters)
                }}

                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: setSelectedIds
                }}
                renderFunctions={{
                    startDate:(value)=> isoDateToDate(value,false),
                    endDate:(value)=> isoDateToDate(value,false),
                    converted: (value , _) =>
                        value ? <Tag color="green">Yes</Tag> : <Button size="small" variant="solid" color="blue" onClick={()=>{setConvertModal(true);setConvertModalData(_)}}>Convert to Event</Button>,

                    rentals: (value, record) => {
                        if (!value || value.length === 0) return "-";
                        return value.map((p) => (
                            <Tag key={p.id} color="blue" style={{ marginBottom: 4 }}>
                                {p.name || p.id} - {record?.rentalCounts?.[p.id] || 1}
                            </Tag>
                        ));
                    }
                }}
                expandable={{
                    expandedRowRender: (record) => `Address : ${record.address}`
                }}
            /></>}
        </>
    )
}

export default LeadsView
